import { appStorage } from '../lib/betaStorage'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { TopBar } from '../components/TopBar'
import { HANDOVER_FIELDS, SOAP_FIELDS, emptyHandoverNote, emptySoapNote } from '../lib/handover'
import { saveBehaviourIncident } from '../lib/behaviourTracking'
import type { HandoverNote, SoapNote } from '../types'

const STORAGE_KEY = 'schub.demo.caseyLeoHandovers'

export function CaseyLeoHandover() {
  const [note, setNote] = useState<HandoverNote>(emptyHandoverNote)
  const [soap, setSoap] = useState<SoapNote>(emptySoapNote)
  const [useSoap, setUseSoap] = useState(false)
  const [incident, setIncident] = useState(false)
  const [incidentType, setIncidentType] = useState('')
  const [antecedent, setAntecedent] = useState('')
  const [behaviour, setBehaviour] = useState('')
  const [response, setResponse] = useState('')
  const [outcome, setOutcome] = useState('')
  const [injury, setInjury] = useState('')
  const [followUp, setFollowUp] = useState('')
  const [saved, setSaved] = useState(false)

  const save = () => {
    const existing = JSON.parse(appStorage.getItem(STORAGE_KEY) || '[]')
    const id = `leo-handover-${Date.now()}`
    const createdAt = new Date().toISOString()
    existing.unshift({
      id,
      participant: 'Leo',
      worker: 'Casey Morgan',
      shift: 'Today · 3:30 PM–6:30 PM · After-school support',
      documentationStyle: useSoap ? 'soap' : 'standard',
      note,
      soap: useSoap ? soap : undefined,
      behaviouralIncident: incident ? { incidentType, antecedent, behaviour, response, outcome, injury, followUp } : undefined,
      createdAt,
    })
    appStorage.setItem(STORAGE_KEY, JSON.stringify(existing))
    if (incident) {
      saveBehaviourIncident({
        id: `leo-behaviour-${Date.now()}`,
        participantId: 'u-leo',
        participant: 'Leo',
        worker: 'Casey Morgan',
        shift: 'Today · 3:30 PM–6:30 PM · After-school support',
        createdAt,
        incidentType,
        antecedent,
        behaviour,
        response,
        outcome,
        injury,
        followUp,
        sourceHandoverId: id,
        sourceType: 'shift_note',
      })
    }
    setSaved(true)
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <TopBar title="Leo’s Shift Notes & Handover" subtitle="Casey’s structured support-worker documentation" />
      <div className="flex flex-col gap-4 px-4 py-4">
        <Link to="/youth-workspace/more" className="w-fit rounded-xl bg-violet-100 px-3 py-2 text-sm font-black text-violet-700">← More</Link>
        <div className="rounded-2xl bg-brand-50 p-4 text-xs font-semibold text-brand-800"><strong>Linked shift:</strong> Today · 3:30 PM–6:30 PM · After-school support with Leo.</div>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
          <div className="flex items-start justify-between gap-3"><div><p className="text-sm font-black text-slate-900">SOAP note</p><p className="mt-1 text-xs text-slate-500">Keep the same Subjective / Objective / Assessment / Plan format used in SCHub shift notes.</p></div><button type="button" onClick={() => setUseSoap(v => !v)} className={`relative h-7 w-12 rounded-full ${useSoap ? 'bg-brand-600' : 'bg-slate-200'}`}><span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition ${useSoap ? 'left-[22px]' : 'left-0.5'}`} /></button></div>
        </section>

        {useSoap && <section className="grid gap-3 rounded-2xl border border-blue-100 bg-blue-50/50 p-4"><p className="text-xs font-black uppercase text-blue-800">SOAP note</p>{SOAP_FIELDS.map(field => <label key={field.key} className="text-xs font-semibold text-slate-600"><span className="mb-1 block">{field.label}</span><textarea className="input" rows={2} value={soap[field.key]} onChange={e => setSoap(s => ({...s,[field.key]:e.target.value}))} placeholder={field.placeholder} /></label>)}</section>}

        <section className="grid gap-3">{HANDOVER_FIELDS.map(field => <label key={field.key} className="text-xs font-semibold text-slate-600"><span className="mb-1 block">{field.label}</span><textarea className="input" rows={field.rows} value={note[field.key]} onChange={e => setNote(n => ({...n,[field.key]:e.target.value}))} placeholder={field.placeholder.replace('Alex', 'Leo')} /></label>)}</section>

        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-card">
          <div className="flex items-start justify-between gap-3"><div><p className="text-sm font-black text-amber-950">Behavioural incident / behaviour of concern</p><p className="mt-1 text-xs font-semibold text-amber-800">Turn this on only if something occurred that should be documented for Leo’s family and authorised team.</p></div><button type="button" onClick={() => setIncident(v => !v)} className={`relative h-7 w-12 rounded-full ${incident ? 'bg-amber-500' : 'bg-slate-200'}`}><span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition ${incident ? 'left-[22px]' : 'left-0.5'}`} /></button></div>
          {incident && <div className="mt-4 grid gap-3"><input className="input" value={incidentType} onChange={e=>setIncidentType(e.target.value)} placeholder="Type of incident / behaviour" /><textarea className="input" rows={2} value={antecedent} onChange={e=>setAntecedent(e.target.value)} placeholder="What happened immediately before? (antecedent / trigger / context)" /><textarea className="input" rows={3} value={behaviour} onChange={e=>setBehaviour(e.target.value)} placeholder="What was observed? Use objective, factual language." /><textarea className="input" rows={3} value={response} onChange={e=>setResponse(e.target.value)} placeholder="What support strategies were used?" /><textarea className="input" rows={2} value={outcome} onChange={e=>setOutcome(e.target.value)} placeholder="Outcome / how Leo recovered or regulated" /><textarea className="input" rows={2} value={injury} onChange={e=>setInjury(e.target.value)} placeholder="Any injury, first aid, property damage or immediate safety issue?" /><textarea className="input" rows={2} value={followUp} onChange={e=>setFollowUp(e.target.value)} placeholder="Who was notified and what follow-up is required?" /><div className="rounded-xl bg-white/80 p-3 text-[11px] font-semibold text-amber-900"><strong>Automatic therapy tracking:</strong> when this is saved, the structured incident data is also added to Leo’s Behavioural Incident Tracking area for authorised Behaviour Support, OT and Speech clinicians. It remains linked back to this shift note.</div></div>}
        </section>

        <button onClick={save} disabled={saved} className="rounded-2xl bg-brand-600 py-3 text-sm font-black text-white disabled:opacity-70">{saved ? 'Saved ✓' : 'Save Leo shift handover'}</button>
        <p className="text-center text-[11px] text-slate-400">Prototype storage is local to this browser until the shared backend is connected.</p>
      </div>
    </div>
  )
}

